import { Stack, Typography } from "@mui/material";
import { type FC } from "react";

import { PlatformIcons } from "~/components/UI/PlatformIcons";
import { SettledElsewhereMark } from "~/components/UI/SettledElsewhereMark";
import { TradeBonus } from "~/feature/event/components/MarketCalculator/components/TradeBonus";
import { getNoPriceText } from "~/feature/event/components/MarketCalculator/utils/getNoPriceText";
import { usePlatformTradeLink } from "~/feature/event/hooks/usePlatformTradeLink";
import {
  type ICalculatedData,
  useMarketCalculator,
} from "~/feature/event/providers/MarketCalculatorProvider";
import { PlatformClickPlacement } from "~/scripts/platformClick";
import { type IAggregatedEvent, type IMarket } from "~/types/event";
import { formatCents } from "~/utils/formatPrice";
import { formatNumber } from "~/utils/formatValue";

import * as Styled from "./TradeOnLinePlatformCard.styles";

interface ITradeOnPlatformCardProps {
  market: IMarket;
  event: IAggregatedEvent;
  calculatedData: ICalculatedData;
  settledElsewhereWarning?: string | null;
}

export const TradeOnLinePlatformCard: FC<ITradeOnPlatformCardProps> = ({
  market,
  event,
  calculatedData,
  settledElsewhereWarning = null,
}) => {
  const { answer } = useMarketCalculator();
  const { href, platformName, handleClick } = usePlatformTradeLink(
    market,
    event,
    PlatformClickPlacement.Calculator
  );
  const answerText = answer ? "YES" : "NO";
  const { entryPrice } = calculatedData;

  return (
    <Styled.Card href={href} target="_blank" onClick={handleClick}>
      <PlatformIcons platforms={market.platform} size={32} />
      <Stack sx={{ width: "100%", gap: 0.2 }}>
        <Styled.TitleLine>
          <Typography variant="body1" sx={{ fontSize: 12, fontWeight: 700 }}>
            Trade on {platformName}
          </Typography>
          {settledElsewhereWarning && (
            <SettledElsewhereMark tooltip={settledElsewhereWarning} />
          )}
        </Styled.TitleLine>
        <TradeBonus platform={market.platform} />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "11px",
            whiteSpace: "wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {entryPrice === null ? (
            getNoPriceText(answer, platformName)
          ) : (
            <>
              Buy {answerText} for <strong>{formatCents(entryPrice)} </strong>
              <br />
              estimated profit{" "}
              <strong>{formatNumber(calculatedData.netProfit)}</strong>
            </>
          )}
        </Typography>
      </Stack>
    </Styled.Card>
  );
};
